import React, { useState, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Bars3Icon } from '@heroicons/react/24/outline';
import {
  IconHome,
  IconHomeCog,
  IconBook,
  IconSchool,
  IconBrandGooglePhotos,
  IconAddressBook,
  IconBroadcast,
} from '@tabler/icons-react';

export function NavBar() {
  const [openSidebar, setOpenSidebar] = useState(false);
  const [activeLink, setActiveLink] = useState('home');
  const [navbarBg, setNavbarBg] = useState('bg-white/50 rounded-b-[80px] mx-2  backdrop-blur-md w-[98%]');
  const [isScrolled, setIsScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const currentPath = location.pathname;

      if (currentPath === '/home') {
        const scrolled = scrollY > 0;
        setIsScrolled(scrolled);
        setNavbarBg(
          scrolled
            ? 'bg-white/80 shadow-lg left-0 mx-0 w-full backdrop-blur-md'
            : 'bg-white/60 rounded-[30px] mx-2 backdrop-blur-md  backdrop-blur-md !w-[98%] mt-5  '
        );
      } else {
        setNavbarBg('bg-white shadow-lg left-0 mx-0 w-full');
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial call to set the background on load
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);

  const toggleSidebar = () => {
    setOpenSidebar(!openSidebar);
  };

  const closeSidebar = () => {
    setOpenSidebar(false);
  };

  const links = [
    { name: 'Home', path: 'home', icon: <IconHome className="h-5 w-5" /> },
    { name: 'About', path: 'about', icon: <IconHomeCog className="h-5 w-5" /> },
    { name: 'Library', path: 'library', icon: <IconBook className="h-5 w-5" /> },
    { name: 'Courses', path: 'courses', icon: <IconSchool className="h-5 w-5" /> },
    { name: 'Gallery', path: 'gallery', icon: <IconBrandGooglePhotos className="h-5 w-5" /> },
    { name: 'Radio', path: 'radio', icon: <IconBroadcast className="h-5 w-5" /> },
    { name: 'Contact', path: 'contact', icon: <IconAddressBook className="h-5 w-5" /> },
  ];

  return (
    <>
      {/* Navbar for screens above 700px */}
      <div
        className={`hidden w-full sm:flex h-[60px] z-[99] ${navbarBg} justify-between  lg:pr-28 md:px-10 px-5 items-center fixed top-0 transition-all duration-300 ease-in-out`}
      >
        <div
          className="logo font-AndoraModernSerif text-2xl flex gap-1 items-center cursor-pointer"
          onClick={() => {
            setActiveLink('home');
            navigate('/home');
          }}
        >
          <img src="/assets/darulLogo.png" alt="logo" className="w-[50px] h-[50px] mb-2" />
          <p className="font-AndoraModernSerif mt-1 text-[#2a1273]">DarulHadees</p>
        </div>
        <div className="flex lg:gap-10 gap-4 md:gap-5 font-AndoraModernSerif">
          {links.map(({ name, path, onclick, ignoreLink }) => {
            if (ignoreLink) {
              return (
                <p
                  key={name}
                  onClick={onclick}
                  className="cursor-pointer font-semibold text-black"
                >
                  {name}
                </p>
              );
            } else {
              return (
                <NavLink
                  key={path}
                  to={path ? `/${path}` : ''}
                  onMouseEnter={() => {
                    // Prefetch the component if it's lazy loaded
                    const routeMap = {
                      'about': () => import('../pages/About/mainAbout'),
                      'contact': () => import('../pages/contact/contactNew'),
                      'courses': () => import('../pages/Courses/page'),
                      'gallery': () => import('../pages/gallary/page'),
                      'radio': () => import('../pages/radio/RadioPage'),
                    };
                    if (routeMap[path]) routeMap[path]();
                  }}
                  className={({ isActive }) =>
                    isActive
                      ? 'text-pumpkinToast-400 font-semibold'
                      : 'text-black font-semibold'
                  }
                >
                  <p className="">{name}</p>
                </NavLink>
              );
            }
          })}
        </div>
      </div>

      {/* Sidebar and Menu Icon for screens below 700px */}
      <div className="flex sm:hidden fixed top-0 w-full h-[60px] bg-white items-center justify-between px-5 z-[40] shadow-lg">
        <div
          className="logo font-semibold text-2xl flex gap-1 items-end cursor-pointer"
          onClick={() => {
            setActiveLink('home');
            navigate('/home');
          }}
        >
          <img src="/assets/darulLogo.png" alt="logo" className="w-[50px] h-[50px]" />
          <p className="text-xl pb-1 font-AndoraModernSerif text-[#2a1273]">DarulHadees</p>
        </div>
        <button onClick={() => setOpenSidebar(true)} style={{ backgroundColor: '#DD9356' }} className="p-2 rounded">
          <Bars3Icon className="h-6 w-6 text-white" />
        </button>
      </div>

      {/* Custom Sidebar */}
      <div
        className={`fixed top-0 left-0 h-full w-[250px] bg-white shadow-2xl z-[101] transform ${openSidebar ? 'translate-x-0' : '-translate-x-full'
          } transition-transform duration-300 ease-in-out`}
      >
        <div className="flex flex-col gap-8 px-7 py-3">
          {/* Logo Section */}
          <div
            className="flex items-end gap-2 cursor-pointer"
            onClick={() => {
              setActiveLink('home');
              navigate('/home');
            }}
          >
            <img src="/assets/darulLogo.png" alt="logo" className="w-[40px] h-[40px]" />
            <p className="font-semibold text-xl font-AndoraModernSerif text-[#2a1273]">DarulHadees</p>
          </div>

          {/* Links Section */}
          <div className="flex flex-col gap-5">
            {links.map(({ name, path, icon, onclick, ignoreLink }) => {
              if (ignoreLink) {
                return (
                  <p
                    key={name}
                    onClick={() => {
                      closeSidebar();
                      onclick();
                    }}
                    className="cursor-pointer flex items-center gap-3 w-full h-[40px] px-4 text-black"
                  >
                    {icon}
                    {name}
                  </p>
                );
              } else {
                return (
                  <NavLink
                    key={path}
                    to={`/${path}`}
                    onMouseEnter={() => {
                      const routeMap = {
                        'about': () => import('../pages/About/mainAbout'),
                        'contact': () => import('../pages/contact/contactNew'),
                        'courses': () => import('../pages/Courses/page'),
                        'gallery': () => import('../pages/gallary/page'),
                        'radio': () => import('../pages/radio/RadioPage'),
                      };
                      if (routeMap[path]) routeMap[path]();
                    }}
                    className={({ isActive }) =>
                      isActive
                        ? 'text-white rounded-md bg-pumpkinToast-500'
                        : 'text-black'
                    }
                    onClick={() => {
                      closeSidebar();
                    }}
                  >
                    <div className="flex items-center gap-3 w-full h-[40px] px-4">
                      {icon}
                      <p className="">{name}</p>
                    </div>
                  </NavLink>
                );
              }
            })}
          </div>
        </div>
      </div>

      {/* Backdrop Blur */}
      {openSidebar && (
        <div
          onClick={closeSidebar}
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[100] transition-opacity duration-300"
        />
      )}
    </>
  );
}
